import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Divider,
  Text,
  Table,
  Group,
  ScrollArea,
  SegmentedControl,
  Button,
  Pagination,
  Box
} from "@mantine/core";
import Swal from "sweetalert2";

import Row from "./RegisteredRow";
import PremiumModal from "../../../app/PremiumModal";
import FilterPanelController from '../../presentations/filters/FilterPanelController';

import MemberModel from "../../../../models/Member";
import ManagerModel from "../../../../models/Manager";
import SpaceMemberFilterModel from "../../../../models/SpaceMemberFilters";

import { setTiptapSidemenu } from "../../../../store/app";
import { useFilterPanel } from "../../../hooks/useFilterPanel";

const paginator = { page: 1, per: 30 };

const loader = {
  loading: false,
  oldScope: "all"
};

export default function Registered(props) {
  const theme = props.theme;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { term, setTerm, sortSelected, filtersSelected, isFilterVisible, setIsFilterVisible } = useFilterPanel();

  const space = useSelector((state) => state.space);
  const tiptapSidemenu = useSelector((state) => state.tiptapSidemenu);
  
  const [members, setMembers] = useState({
    total: 0,
    page: 1,
    pages: 1,
    data: []
  });
  const [counts, setCounts] = useState({
    all: 0,
    active: 0,
    pending: 0,
    managers: 0
  });
  const [scope, setScope] = useState(null);
  const [opened, setOpened] = useState(false);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [filtersVersion, setFiltersVersion] = useState(0);
  const [paymentFilters, setPaymentFilters] = useState({});
  const [filtersOptionCounts, setFiltersOptionCounts] = useState([]);
  const [filtersTopLocations, setFiltersTopLocations] = useState([]);

  const timeoutId = useRef();

  const onSelect = (id) => {
    const newSelected = [...selected];
    const index = selected.indexOf(id);
    if (index >= 0) {
      newSelected.splice(index, 1);
    } else {
      newSelected.push(id);
    }
    setSelected(newSelected);
  };

  const onSelectAll = (all) => {
    const newSelected = [];
    if (all) {
      for (let i = 0; i < members.data.length; i++) {
        newSelected.push(members.data[i].username);
      }
    }
    setSelected(newSelected);
  };

  const areAllSelected = () => {
    return members.data.length == selected.length;
  };

  const hasSelections = () => {
    return selected.length > 0;
  };

  const isAnySelected = () => {
    return !areAllSelected() && selected.length > 0;
  };

  const isSelected = (id) => {
    return selected.indexOf(id) >= 0;
  };

  const canAddNewManagers = () => {
    const valid =
      counts.managers < space.permissions.max_allowed_managers[0] ||
      space.permissions.max_allowed_managers[0] == -1;
    if (!valid) {
      setOpened([
        true,
        space.permissions.max_allowed_managers[1],
        space.permissions.max_allowed_managers[2],
      ]);
    }
    return valid;
  };

  useEffect(() => {
    if (space && space.slug) {
      paginator.page = 1;
      onFetch();
    }
  }, [space, term, scope]);

  useEffect(() => {
    if (space && space.slug) {
      onFetchCounts();
      onFetchFilters();
      onFetchPaymentFilters();
      onFetchOptionCounts();
      onFetchTopLocations();
    }
  }, [space, scope]);

  useEffect(() => {
    if (tiptapSidemenu && tiptapSidemenu.opened == "filter-members-close") {
      dispatch(setTiptapSidemenu({ opened: null }));
      onFetchFilters();
      onFetch();
      onFetchOptionCounts();
      onFetchTopLocations();
    }
  }, [tiptapSidemenu]);

  const onFetch = () => {
    setLoading(true);
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      if (loader.oldScope != scope) {
        paginator.page = 1;
        loader.oldScope = scope;
      }

      const _filtersSelected = Array.isArray(filtersSelected.current) ? filtersSelected.current : [];
      const _mainFiltersSelected = _filtersSelected.filter(f => !["bundle", "coupon", "payment_survey"].includes(f.id));

      const filter_ids = _mainFiltersSelected.map((item) => ({
        id: item.id,
        values: item.selectedOptions || [],
        condition: item.condition
      }));

      const bundle_ids = _filtersSelected.filter(f => f.id === "bundle").map((item) => ({
        id: item.id,
        values: item.selectedOptions || [],
        condition: item.condition
      }));

      const coupon_ids = _filtersSelected.filter(f => f.id === "coupon").map((item) => ({
        id: item.id,
        values: item.selectedOptions || [],
        condition: item.condition
      }));

      const flexible_ids = _filtersSelected.filter(f => f.id === "payment_survey").map((item) => ({
        id: item.id,
        values: item.selectedOptions || [],
        condition: item.condition
      }));

      const sort = Array.isArray(sortSelected.current) ? sortSelected.current.map((item) => ({
        id: item.id,
        value: item.value,
        sort_order: item.sort_order
      })) : [];

      const Model = scope != "managers" ? MemberModel : ManagerModel;
      Model.onFetch(
        space.slug,
        paginator,
        {
          scope,
          term,
          filter_ids,
          sort,
          bundle_ids,
          coupon_ids,
          flexible_ids
        },
        (data) => {
          setLoading(false);
          setMembers(data);
        },
        (data) => {
          setLoading(false);
        }
      );
    }, 1000);
  };

  const onFetchCounts = () => {
    MemberModel.onFetchCounts(
      space.slug,
      (data) => {
        setCounts(data);
      },
      (data) => {}
    );
  };

  const onFetchFilters = () => {
    if (!window.$fetchedTopSlug) return;
    SpaceMemberFilterModel.onFetch(
      window.$fetchedTopSlug,
      (data) => {
        if (Array.isArray(data)) {
          setFilters(data);
        }
      },
      (error) => {}
    );
  };

  const onFetchPaymentFilters = () => {
    if (!space.slug) return;
    SpaceMemberFilterModel.onFetchPaymentFilters(
      space.slug,
      (data) => setPaymentFilters(data),
      (error) => {}
    );
  };

  const onFetchOptionCounts = () => {
    if (!space.slug) return;
    SpaceMemberFilterModel.onGetOptionCounts(
      space.slug,
      (data) => {
        if (Array.isArray(data)) {
          setFiltersOptionCounts(data);
        }
      },
      (error) => {}
    );
  };

  const onFetchTopLocations = () => {
    if (!space.slug) return;
    SpaceMemberFilterModel.onGetTopLocations(
      space.slug,
      (data) => {
        if (Array.isArray(data)) {
          setFiltersTopLocations(data);
        }
      },
      (error) => {}
    );
  };

  const onSelectFilter = (filter) => {
    window.$dropdownFilterId = filter.id;
    filtersSelected.current = Array.isArray(filtersSelected.current) ? [...filtersSelected.current, filter] : [filter];
    // To update UI
    setFiltersVersion((v) => v + 1);
  };

  const onUpdateFilter = (filter) => {
    let updatedFilters = [];
    if (Array.isArray(filtersSelected.current) && filter) {
      if (Array.isArray(filter)) {
        updatedFilters = filter;
      } else {
        if (filtersSelected.current.length === 0) {
          updatedFilters = [filter]
        } else {
          const isExisting = filtersSelected.current.find((item) => item.id === filter.id);
          updatedFilters = isExisting ? filtersSelected.current.map((item) => {
            return item.id === filter.id ? filter : item;
          }) : [...filtersSelected.current, filter];
        }
      }
    }
    filtersSelected.current = updatedFilters;
    paginator.page = 1;
    onFetch();
  };

  const onUpdateSort = (sort) => {
    sortSelected.current = sort ? sort : [];
    paginator.page = 1;
    onFetch();
    // To update UI
    setFiltersVersion((v) => v + 1);
  };

  const changePage = (page) => {
    paginator.page = page;
    onFetch();
  };

  const onApproveAll = () => {
    MemberModel.onApproveAll(
      space.slug,
      (data) => {
        onFetch();
        onFetchCounts();
      },
      (data) => {}
    );
  };

  const onDestroyConfirm = () => {
    Swal.fire({
      title: "Are you sure?",
      html: "You are about to remove the selected members",
      showDenyButton: true,
      confirmButtonText: "Remove the selected members",
      denyButtonText: "Cancel",
      confirmButtonColor: theme.colors.red[8],
      denyButtonColor: theme.colors.gray[6],
    }).then((result) => {
      if (result.isConfirmed) {
        onDestroyAllSelected();
      }
    });
  };

  const onDestroyAllSelected = () => {
    const member_ids = selected.map((username) => {
      return members.data.find((item) => item.username == username)?.user_id || null;
    }).filter((item) => item != null);
    MemberModel.onDestroyAllSelected(
      space.slug,
      member_ids,
      (data) => {
        onFetch();
        onFetchCounts();
      },
      (err) => {}
    );
  };

  return (
    <div>
      <FilterPanelController
        type="filter-members"
        section="members-settings"
        loading={loading}
        term={term}
        filters={filters}
        filtersSelected={filtersSelected.current}
        sortSelected={sortSelected.current}
        paymentFilters={paymentFilters}
        optionCounts={filtersOptionCounts}
        topLocations={filtersTopLocations}
        isFilterVisible={isFilterVisible}
        setTerm={setTerm}
        onSelectFilter={onSelectFilter}
        onUpdateFilter={onUpdateFilter}
        onUpdateSort={onUpdateSort}
        setIsFilterVisible={setIsFilterVisible}
        RightComponent={() => (
          <Group position="right" spacing="xs">
            {!hasSelections() && (
              <SegmentedControl
                value={scope}
                onChange={setScope}
                radius="xl"
                sx={(theme) => ({ backgroundColor: theme.colors.gray[0] })}
                data={[
                  {
                    label: `${t(
                      "spaces.settings.members.registered.registered.all"
                    )} (${counts.all})`,
                    value: "all",
                  },
                  {
                    label: `${t(
                      "spaces.settings.members.registered.registered.active"
                    )} (${counts.active})`,
                    value: "active",
                  },
                  {
                    label: `${t(
                      "spaces.settings.members.registered.registered.pending"
                    )} (${counts.pending})`,
                    value: "pending",
                  },
                  {
                    label: `${t(
                      "spaces.settings.members.registered.registered.manager"
                    )} (${counts.managers})`,
                    value: "managers",
                  },
                ]}
              />
            )}
            {hasSelections() && (
              <>
                <Button
                  radius="xl"
                  sx={{
                    backgroundColor: theme.colors.sutrablue[3],
                    "&:hover": {
                      backgroundColor: theme.colors.sutrablue[4],
                    },
                  }}
                  onClick={(event) => props.onSendBroadcast("email", members.data, selected)}
                >
                  {t("spaces.settings.members.registered.registered.email")}
                </Button>
                <Button
                  radius="xl"
                  sx={{
                    backgroundColor: theme.colors.sutrablue[3],
                    "&:hover": {
                      backgroundColor: theme.colors.sutrablue[4],
                    },
                  }}
                  onClick={(event) => props.onSendBroadcast("message", members.data, selected)}
                >
                  {t("spaces.settings.members.registered.registered.message")}
                </Button>
                <Button
                  radius="xl"
                  sx={{
                    backgroundColor: theme.colors.red[4],
                    "&:hover": {
                      backgroundColor: theme.colors.red[5],
                    },
                  }}
                  onClick={onDestroyConfirm}
                >
                  {"Delete"}
                </Button>
              </>
            )}
          </Group>
        )}
      />

      <ScrollArea mt={10}>
        {members.data.length == 0 ? (
          <Box
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === "dark"
                  ? theme.colors.dark[6]
                  : theme.colors.gray[0],
              textAlign: "center",
              padding: theme.spacing.xl,
              borderRadius: theme.radius.md,
              cursor: "pointer",

              "&:hover": {
                backgroundColor:
                  theme.colorScheme === "dark"
                    ? theme.colors.dark[5]
                    : theme.colors.gray[1],
              },
            })}
          >
            {t("spaces.settings.members.registered.registered.noRecordFound")}
          </Box>
        ) : (
          <Table verticalSpacing="xs" highlightOnHover>
            <thead>
              <tr>
                <th>
                  <Checkbox
                    checked={areAllSelected()}
                    indeterminate={isAnySelected()}
                    onChange={(event) => onSelectAll(event.currentTarget.checked)}
                  />
                </th>
                <th>
                  {t("spaces.settings.members.registered.registeredTable.member")}
                </th>
                <th>
                  {t("spaces.settings.members.registered.registeredTable.email")}
                </th>
                <th>
                  {t("spaces.settings.members.registered.registeredTable.status")}
                </th>
                <th colspan="2">
                  <Group position="apart">
                    <Text size="sm">
                      {t("spaces.settings.members.registered.registeredTable.role")}
                    </Text>
                    <Button
                      color="green"
                      variant="outline"
                      radius="xl"
                      size="xs"
                      compact
                      onClick={() => onApproveAll()}
                    >
                      {t("spaces.settings.members.registered.registeredTable.roleApproveAll")}
                    </Button>
                  </Group>
                </th>
              </tr>
            </thead>
            <tbody>
              {members.data.map((member) => {
                return (
                  <Row
                    hasSubscriptions={!!space?.permissions?.can_accept_charges}
                    spaceId={space.slug}
                    onUpdate={onFetchCounts}
                    member={member}
                    canAddNewManagers={canAddNewManagers}
                    isSelected={isSelected}
                    onSelect={onSelect}
                    scope={scope}
                  />
                );
              })}
            </tbody>
          </Table>
        )}
      </ScrollArea>
      <Divider sx={(theme) => ({ borderTopColor: theme.colors.gray[2] })} />

      <Box sx={{ width: "100%", display: "flex", marginTop: 20, justifyContent: "center" }}>
        <Pagination
          color="gray"
          page={paginator.page}
          onChange={changePage}
          total={members.pages}
        />
      </Box>

      <PremiumModal opened={opened} setOpened={setOpened} />
    </div>
  );
}
